import ProspectStageTagRenderer from '@app/components/ProspectStageTagRenderer';
import TaskSubjectRenderer from '@app/components/TaskSubjectRenderer';
import { SYSTEM_ATTRIBUTE_SCHEMA_NAMES } from '@app/data/manageLeads/leadAttributes';
import { type ModifiedColumn } from '@app/store/table/tableSlice';
import { type DataSourceItem } from '@app/utils/manageTasks/taskListTable';
import { translate, TranslationEntity } from '@repo/utils';
import { Tag } from 'antd';
import dayjs from 'dayjs';

export const TASK_ATTRIBUTE_SCHEMA_NAMES = {
  id: 'id',
  subject: 'subject',
  description: 'description',
  startTime: 'start_time',
  endTime: 'end_time',
  status: 'status',
  reminderConfig: 'reminder_config',
  owner: 'owner',
  lead: 'lead',
  organizer: 'organizer',
  completedBy: 'completed_by',
  completedOn: 'completed_on',
  config: 'config',
  taskType: 'template',
  template: 'template',
  createdAt: 'created_at',
};

export type TaskSchemaNameType = keyof typeof TASK_ATTRIBUTE_SCHEMA_NAMES;

export const DEFAULT_VISIBLE_COLUMNS = [
  TASK_ATTRIBUTE_SCHEMA_NAMES.id,
  TASK_ATTRIBUTE_SCHEMA_NAMES.subject,
  TASK_ATTRIBUTE_SCHEMA_NAMES.taskType,
  TASK_ATTRIBUTE_SCHEMA_NAMES.status,
  TASK_ATTRIBUTE_SCHEMA_NAMES.endTime,
  TASK_ATTRIBUTE_SCHEMA_NAMES.owner,
  TASK_ATTRIBUTE_SCHEMA_NAMES.createdAt,
];

export const SORTABLE_FIELDS = [
  TASK_ATTRIBUTE_SCHEMA_NAMES.id,
  TASK_ATTRIBUTE_SCHEMA_NAMES.subject,
  TASK_ATTRIBUTE_SCHEMA_NAMES.endTime,
  TASK_ATTRIBUTE_SCHEMA_NAMES.description,
  TASK_ATTRIBUTE_SCHEMA_NAMES.completedOn,
  TASK_ATTRIBUTE_SCHEMA_NAMES.startTime,
  TASK_ATTRIBUTE_SCHEMA_NAMES.createdAt,
];

export const SORTABLE_FIELD_MAPPING = {
  [TASK_ATTRIBUTE_SCHEMA_NAMES.id]: 'task_id',
  [TASK_ATTRIBUTE_SCHEMA_NAMES.subject]: 'subject',
  [TASK_ATTRIBUTE_SCHEMA_NAMES.startTime]: 'start_time',
  [TASK_ATTRIBUTE_SCHEMA_NAMES.endTime]: 'end_time',
  [TASK_ATTRIBUTE_SCHEMA_NAMES.description]: 'description',
  [TASK_ATTRIBUTE_SCHEMA_NAMES.completedOn]: 'completed_on',
  [TASK_ATTRIBUTE_SCHEMA_NAMES.createdAt]: 'created_at',
};

const reminderRenderer = (reminderConfig: any) => {
  const {
    seconds,
    beforeDays,
    atTime,
  } = reminderConfig || {};

  if (seconds) {
    return `${Math.ceil(seconds / 60)} minutes`;
  } else if (atTime) {
    if (beforeDays) {
      return `${beforeDays} day(s) before at ${dayjs(atTime, 'HHmm').format('HH:mm a')}`;
    } else {
      return `On Due date at ${dayjs(atTime, 'HHmm').format('HH:mm a')}`;
    }
  } else {
    return 'None';
  }
};

export const TASK_ATTRIBUTE_CONFIG : Record<string, Partial<ModifiedColumn>> = {
  [TASK_ATTRIBUTE_SCHEMA_NAMES.id]: {
    title: translate(`#{${TranslationEntity.task}} ID`, [{
      capitalize: true,
    }]),
    dataType: 'string',
    canHide: false,
    canReorder: true,
    canResize: true,
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.subject]: {
    title: translate(`#{${TranslationEntity.task}}`, [{
      capitalize: true,
    }]),
    dataType: 'string',
    render: (_: any, record: any) => (
      <TaskSubjectRenderer
        subject={record.subject}
        status={record.status}
        leadUuid={record.lead.leadUuid}
        firstName={record[`lead.${SYSTEM_ATTRIBUTE_SCHEMA_NAMES.firstName}`]}
        lastName={record[`lead.${SYSTEM_ATTRIBUTE_SCHEMA_NAMES.lastName}`]}
      />
    ),
    canHide: false,
    canReorder: true,
    canResize: true,
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.description]: {
    title: 'Description',
    dataType: 'string',
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.startTime]: {
    title: 'Due Date',
    dataType: 'datetime',
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.endTime]: {
    title: 'End Date',
    dataType: 'datetime',
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.status]: {
    title: 'Status',
    dataType: 'string',
    render: (status: string) => (
      <div
        style={{
          textTransform: 'capitalize',
        }}
      >
        {status}
      </div>
    ),
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.reminderConfig]: {
    title: 'Email Reminder',
    dataType: 'string',
    render: (reminderConfig: any) => reminderRenderer(reminderConfig),
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.owner]: {
    title: translate(`#{${TranslationEntity.task}} Owner`, [{
      capitalize: true,
    }]),
    dataType: 'string',
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.organizer]: {
    title: 'Organizer',
    dataType: 'string',
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.completedBy]: {
    title: 'Completed By',
    dataType: 'string',
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.template]: {
    title: 'Template',
    dataType: 'string',
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.taskType]: {
    title: translate(`#{${TranslationEntity.task}} Type`, [{
      capitalize: true,
    }]),
    dataType: 'string',
    render: (template: any) => <Tag color={template.config?.colorScheme}>{template.name}</Tag>,
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.completedOn]: {
    title: 'Completed On',
    dataType: 'datetime',
  },
  [TASK_ATTRIBUTE_SCHEMA_NAMES.createdAt]: {
    title: 'Created On',
    dataType: 'datetime',
  },
};

export const FIELDS_WITH_USER_REFERENCE = [
  TASK_ATTRIBUTE_SCHEMA_NAMES.owner,
  TASK_ATTRIBUTE_SCHEMA_NAMES.organizer,
  TASK_ATTRIBUTE_SCHEMA_NAMES.completedBy,
];

export const LEAD_ATTRIBUTE_CONFIG = {
  [SYSTEM_ATTRIBUTE_SCHEMA_NAMES.leadStage]: {
    render: (_: any, record: DataSourceItem) => {
      const schemaName = `lead.${SYSTEM_ATTRIBUTE_SCHEMA_NAMES.leadStage}`;
      const leadStage = record.task.attributes[schemaName];

      return (
        <ProspectStageTagRenderer
          leadStage={leadStage}
        />
      );
    },
  },
  [SYSTEM_ATTRIBUTE_SCHEMA_NAMES.email]: {
    render: (email: any) => email,
  },
};
